export const content = {
  en: {
    nav: { about: 'About', experience: 'Experience', skills: 'Skills', projects: 'Projects', education: 'Education', contact: 'Contact' },
    hero: {
      name: 'Jordan Rivera Rodriguez',
      titles: ['Software Engineer', 'Full-Stack Developer', 'Security Researcher', 'Data Analyst', 'DevOps Engineer'],
      subtitle: 'Building secure, scalable systems - from enterprise platforms to game server architecture',
      cta: 'View My Work',
      resume: 'Get In Touch',
    },
    about: {
      title: 'About Me',
      paragraphs: [
        'Software Engineer (recent graduate, degree in progress) with 8+ years of professional IT experience spanning system administration, data analysis, full-stack development, cybersecurity research, and game technology.',
        'Proven leadership in cross-functional technical teams, with deep expertise in reverse engineering, penetration testing, security hardening, and modern cloud-native architectures. Passionate about understanding systems at their lowest level - from binary analysis to infrastructure defense.',
        'Fluent in Spanish and English. Remote contractor · In-person meetings in Southern California holder. Available for remote collaboration, contract engagements, and on-site meetings.',
      ],
      stats: [
        { value: '8+', label: 'Years Experience' },
        { value: '50+', label: 'Projects Delivered' },
        { value: '15+', label: 'Technologies Mastered' },
        { value: '2', label: 'Languages Fluent' },
      ],
    },
    experience: {
      title: 'Professional Experience',
      jobs: [
        {
          title: 'Offensive Security Engineer',
          company: 'SQUEMA (squema.group)',
          location: 'Baja California, Mexico · Remote',
          period: 'January 2026 – May 2026',
          points: [
            'Founder of Squema Group (squema.group), an offensive security consultancy with a primarily international client base — from overseas markets to Mexico and Latin America',
            'Specialized in penetration testing for web applications, REST/GraphQL APIs, and e-commerce platforms',
            'Designed engagement methodologies aligned with OWASP Top 10, OWASP API Security, and PTES standards',
            'Built internal tooling and a private pentesting platform for engagement management, reporting, and finding tracking',
            'Lead client communications, scoping, and executive-level reporting in both Spanish and English',
          ],
        },
        {
          title: 'Technology Consultant',
          company: 'Lumina',
          location: 'Remote',
          period: 'January 2026 – Present',
          points: [
            'Technology consultant advising on architecture decisions, stack selection, and engineering practices',
            'Evaluate and recommend modern web, data, and cloud technologies aligned with business goals',
            'Provide guidance on security posture, infrastructure design, and developer tooling',
            'Translate technical trade-offs into actionable recommendations for non-technical stakeholders',
          ],
        },
        {
          title: 'Head of Data Analysis & IT Systems',
          company: 'Grupo Gruremex y Ravizza',
          location: 'Mexicali, Mexico',
          period: 'August 2024 – April 2026',
          points: [
            'Led data analysis initiatives for a restaurant group with 15+ locations across multiple brands',
            'Designed and maintained automated reporting systems, executive KPI dashboards, and predictive analytics models using Power BI and custom Python pipelines',
            'Reported directly to ownership, translating complex datasets into actionable business strategies that drove revenue growth',
            'Built full-stack enterprise platform: sales analytics, employee management with facial recognition, inventory control, and recipe costing',
            'Implemented data-driven decision making processes that improved operational efficiency across all locations',
          ],
        },
        {
          title: 'Systems Lead & Technical Consultant',
          company: 'Consultores IDEA - Political Consulting',
          location: 'Mexico City',
          period: 'June 2023 – June 2024',
          points: [
            'Led technical operations and team coordination for the 2024 Baja California state election campaign',
            'Designed and implemented custom software solutions for campaign data management and voter analytics',
            'Managed cross-functional teams of 10+ under strict deadlines in a high-stakes environment',
            'Implemented security protocols for sensitive campaign data including encryption, access control, and audit logging',
            'Built real-time dashboards for tracking campaign performance metrics across multiple municipalities',
          ],
        },
        {
          title: 'IT Consultant - Photogrammetry & Systems',
          company: 'Independent Practice',
          location: 'Mexicali, Mexico',
          period: 'March 2020 – May 2022',
          points: [
            'Developed custom Python and JavaScript solutions for diverse client requirements across industries',
            'Operated drone and LiDAR equipment; processed and analyzed 3D point cloud datasets for surveying and construction',
            'Designed enterprise networks, VPNs, firewall configurations, and end-to-end infrastructure hardening',
            'Conducted penetration testing and vulnerability assessments for small-to-medium businesses',
            'Delivered technical training sessions on system administration and security best practices',
          ],
        },
        {
          title: 'IT Systems Administrator',
          company: 'MinStock',
          location: 'Panama City, Panama',
          period: 'January 2019 – March 2020',
          points: [
            'Managed company-wide IT infrastructure: Linux/Windows servers, networks, and 50+ user systems',
            'Developed automation scripts for system maintenance, monitoring, log analysis, and incident response',
            'Built internal web tools (dashboards, ticketing system) and maintained QA testing workflows with CI/CD pipelines',
            'Implemented intrusion detection, security monitoring, and disaster recovery procedures',
            'Reduced system downtime by 40% through proactive monitoring and automated alerting',
          ],
        },
        {
          title: 'Computer Technician',
          company: 'Servicios Técnicos Carranza',
          location: 'Mexicali, Mexico',
          period: 'December 2015 – December 2018',
          points: [
            'Hardware diagnostics, repair, custom PC builds, and preventive maintenance for 100+ clients',
            'Network configuration, OS deployment (Linux and Windows), and data recovery services',
            'Malware removal, system hardening, and security auditing for small business environments',
          ],
        },
      ],
    },
    skills: {
      title: 'Technical Expertise',
      categories: [
        {
          name: 'Full-Stack Development',
          icon: '💻',
          items: ['React', 'Next.js', 'TypeScript', 'Node.js', 'Python', 'FastAPI', 'Django', 'C#', '.NET', 'PHP', 'SQL', 'REST APIs', 'GraphQL', 'HTML/CSS', 'Tailwind CSS', 'Shell/Bash', 'Git'],
        },
        {
          name: 'Game Development & Engines',
          icon: '🎮',
          items: ['Unity', 'Gamebryo / NetImmerse', 'Blender', 'Unreal Engine', 'Godot', 'C++', 'C#', 'Lua', 'HLSL/GLSL Shaders', '3D Asset Pipelines', 'Multiplayer Networking', 'Game Architecture', 'Physics Systems'],
        },
        {
          name: 'Cybersecurity & Offensive Security',
          icon: '🔒',
          items: ['IDA Pro', 'Ghidra', 'Burp Suite', 'Metasploit', 'Nmap', 'Wireshark', 'x64dbg', 'John the Ripper', 'Hashcat', 'sqlmap', 'Penetration Testing', 'Binary Analysis', 'Protocol Reverse Engineering', 'Malware Analysis', 'Vulnerability Assessment', 'Network Security', 'OWASP Top 10', 'Infrastructure Hardening', 'Incident Response', 'CTF Competitions'],
        },
        {
          name: 'Data & Analytics',
          icon: '📊',
          items: ['MongoDB', 'PostgreSQL', 'SQL Server', 'MySQL', 'Redis', 'Elasticsearch', 'Power BI', 'Python/Pandas', 'NumPy', 'Data Analytics', 'KPI Dashboards', 'ETL Pipelines', 'Data Visualization', 'Predictive Modeling'],
        },
        {
          name: '3D & Visualization',
          icon: '🎨',
          items: ['Blender (Modeling, Rigging, Animation)', 'AutoCAD', 'Photogrammetry', 'Point Cloud Processing', 'VR/AR Development', 'VRChat SDK', 'UdonSharp'],
        },
        {
          name: 'DevOps & Cloud',
          icon: '☁️',
          items: ['Linux Administration', 'Windows Server', 'Docker', 'Kubernetes', 'AWS', 'Azure', 'GCP', 'Nginx', 'Apache', 'CI/CD', 'GitHub Actions', 'Git', 'Terraform', 'Ansible', 'Grafana', 'Prometheus', 'Cloudflare'],
        },
      ],
    },
    projects: {
      title: 'Selected Projects',
      items: [
        {
          name: 'Gruremex Enterprise Platform',
          description: 'Full-stack enterprise platform for restaurant operations with 15+ locations. Sales and expense analytics, employee management with facial recognition, inventory control, recipe costing, and multi-brand reporting dashboards.',
          tech: ['FastAPI', 'MongoDB', 'Next.js 16', 'Python', 'TypeScript'],
          icon: '🏢',
        },
        {
          name: 'Capacita - Learning Management System',
          description: 'Complete LMS for the government of Baja California. 12 interactive activity types, evaluations with 2FA, PGP-signed certificates, PWA offline, i18n bilingual, real-time notifications, WCAG 2.1 AA+ accessibility. 100+ issues audited and resolved.',
          tech: ['Next.js 16', 'MongoDB', 'TypeScript', 'React-PDF', 'DDD Architecture'],
          icon: '📚',
        },
        {
          name: 'MMORPG Systems & Live-Service Architecture',
          description: 'In-depth study of MMORPG server architecture and live-service systems: authentication, character and zone flow, real-time combat, party/guild systems, NPC behavior with pathfinding, and scalable database patterns.',
          tech: ['C#', '.NET', 'Game Systems Design', 'Network Programming', 'Live-Service Architecture'],
          icon: '⚔️',
        },
        {
          name: 'Ragnarok Online 2 — Community Player Tools',
          description: 'A suite of community-facing tools for MMORPG players: enhancement ("honing") probability calculators, item databases and scanners, and a live in-game chat viewer. Built from deep, first-hand knowledge of the game economy and progression, with performance-critical backends and modern web frontends.',
          tech: ['C++', 'Next.js', 'MongoDB', 'Redis', 'WebSocket'],
          icon: '🛠️',
        },
        {
          name: 'Open Source Contributions',
          description: 'Blender add-ons: Edge Slide Distance and TopoLens (Edit-mode topology overlay). Open-source developer tooling published on GitHub.',
          tech: ['Python', 'C#', 'Blender API'],
          icon: '🌐',
        },
        {
          name: 'Gamebryo Engine Research & Tooling',
          description: 'In-depth knowledge of Gamebryo/NetImmerse engine architecture and asset formats (NIF, KF, KFM): 3D models, skeletal animations, and scene data. Built Blender import/export tooling, localization utilities, and led modernization of legacy game projects.',
          tech: ['Python', 'Blender', 'NIF/KF/KFM', '3D Pipelines', 'Localization'],
          icon: '🔬',
        },
      ],
    },
    education: {
      title: 'Education',
      items: [
        {
          degree: 'B.S. in Computer Systems Engineering',
          school: 'Instituto Tecnológico de Mexicali',
          period: '2019 – 2026 · Graduate (degree in progress)',
          details: 'Focus: Software Architecture, Database Systems, Network Security, Distributed Systems',
        },
        {
          degree: 'High School Diploma',
          school: 'Preparatoria y Universidad 16 de Septiembre',
          period: 'Graduated July 2019',
          details: '',
        },
      ],
      certifications: 'Continuous self-directed learning in penetration testing, reverse engineering, cloud architecture, game engine internals, and modern full-stack development.',
    },
    contact: {
      title: "Let's Work Together",
      subtitle: 'Remote work for US & international clients. Available for in-person meetings and consultations in Southern California. Same timezone as California (PST/PDT).',
      email: 'jordan@squema.group',
      phone: '+52 (686) 127-8767',
      discord: 'JordanRO2',
      location: 'Mexicali, BC, Mexico',
      visa: 'Remote contractor · In-person meetings in Southern California',
      links: {
        linkedin: 'linkedin.com/in/jordanro2',
        github: 'github.com/JordanRO2',
        website: 'jordan.booth-stash.com',
        squema: 'squema.group',
      },
    },
  },
  es: {
    nav: { about: 'Sobre Mí', experience: 'Experiencia', skills: 'Habilidades', projects: 'Proyectos', education: 'Educación', contact: 'Contacto' },
    hero: {
      name: 'Jordan Rivera Rodriguez',
      titles: ['Ingeniero de Software', 'Desarrollador Full-Stack', 'Investigador de Seguridad', 'Analista de Datos', 'Ingeniero DevOps'],
      subtitle: 'Construyendo sistemas seguros y escalables - desde plataformas empresariales hasta arquitectura de servidores de juegos',
      cta: 'Ver Mi Trabajo',
      resume: 'Contáctame',
    },
    about: {
      title: 'Sobre Mí',
      paragraphs: [
        'Ingeniero en Sistemas Computacionales egresado (título en trámite) con 8+ años de experiencia profesional en administración de sistemas, análisis de datos, desarrollo full-stack, investigación en ciberseguridad y tecnología de videojuegos.',
        'Liderazgo comprobado en equipos técnicos multidisciplinarios, con experiencia profunda en ingeniería inversa, pruebas de penetración, hardening de seguridad y arquitecturas cloud modernas. Apasionado por entender los sistemas a su nivel más bajo - desde análisis binario hasta defensa de infraestructura.',
        'Bilingüe español-inglés. Visa B1/B2 de Estados Unidos. Disponible para colaboración remota, proyectos por contrato y reuniones presenciales.',
      ],
      stats: [
        { value: '8+', label: 'Años de Experiencia' },
        { value: '50+', label: 'Proyectos Entregados' },
        { value: '15+', label: 'Tecnologías Dominadas' },
        { value: '2', label: 'Idiomas Fluidos' },
      ],
    },
    experience: {
      title: 'Experiencia Profesional',
      jobs: [
        {
          title: 'Offensive Security Engineer',
          company: 'SQUEMA (squema.group)',
          location: 'Baja California, México · Remoto',
          period: 'Enero 2026 – Mayo 2026',
          points: [
            'Fundador de Squema Group (squema.group), consultora de seguridad ofensiva con una base de clientes principalmente internacional — de mercados overseas a México y Latinoamérica',
            'Especializado en pentesting de aplicaciones web, APIs REST/GraphQL y plataformas de e-commerce',
            'Diseño de metodologías de engagement alineadas con OWASP Top 10, OWASP API Security y estándares PTES',
            'Construcción de herramientas internas y plataforma privada de pentesting para gestión de engagements, reportería y seguimiento de hallazgos',
            'Comunicación con clientes, definición de scope y reportes ejecutivos en español e inglés',
          ],
        },
        {
          title: 'Consultor de Tecnologías',
          company: 'Lumina',
          location: 'Remoto',
          period: 'Enero 2026 – Presente',
          points: [
            'Consultor de tecnologías asesorando en decisiones de arquitectura, selección de stack y prácticas de ingeniería',
            'Evaluación y recomendación de tecnologías web, de datos y cloud alineadas con objetivos de negocio',
            'Asesoría en postura de seguridad, diseño de infraestructura y tooling de desarrollo',
            'Traducción de trade-offs técnicos en recomendaciones accionables para stakeholders no técnicos',
          ],
        },
        {
          title: 'Jefe de Análisis de Datos y Sistemas IT',
          company: 'Grupo Gruremex y Ravizza',
          location: 'Mexicali, México',
          period: 'Agosto 2024 – Abril 2026',
          points: [
            'Lideré iniciativas de análisis de datos para un grupo restaurantero con 15+ ubicaciones',
            'Diseñé y mantuve sistemas de reporteo automatizado, dashboards ejecutivos de KPIs y modelos predictivos con Power BI y pipelines Python',
            'Reporté directamente a los dueños, traduciendo datasets complejos en estrategias de negocio accionables',
            'Construí plataforma empresarial full-stack: analítica de ventas, gestión de empleados con reconocimiento facial, control de inventario y costeo de recetas',
            'Implementé procesos de toma de decisiones basados en datos que mejoraron la eficiencia operativa',
          ],
        },
        {
          title: 'Líder de Sistemas y Consultor Técnico',
          company: 'Consultores IDEA - Consultoría Política',
          location: 'Ciudad de México',
          period: 'Junio 2023 – Junio 2024',
          points: [
            'Lideré operaciones técnicas y coordinación de equipo para la campaña electoral 2024 de Baja California',
            'Diseñé e implementé soluciones de software para gestión de datos de campaña y analítica electoral',
            'Gestioné equipos multidisciplinarios de 10+ personas bajo plazos estrictos en entorno de alto riesgo',
            'Implementé protocolos de seguridad para datos sensibles incluyendo cifrado, control de acceso y auditoría',
            'Construí dashboards en tiempo real para seguimiento de métricas de campaña por municipio',
          ],
        },
        {
          title: 'Consultor IT - Fotogrametría y Sistemas',
          company: 'Práctica Independiente',
          location: 'Mexicali, México',
          period: 'Marzo 2020 – Mayo 2022',
          points: [
            'Desarrollé soluciones personalizadas en Python y JavaScript para diversos requerimientos de clientes',
            'Operé equipo de drones y LiDAR; procesé y analicé datasets de nubes de puntos 3D para topografía y construcción',
            'Diseñé redes empresariales, VPNs, configuración de firewalls y hardening integral de infraestructura',
            'Realicé pruebas de penetración y evaluaciones de vulnerabilidad para PyMEs',
            'Impartí capacitaciones técnicas sobre administración de sistemas y mejores prácticas de seguridad',
          ],
        },
        {
          title: 'Administrador de Sistemas IT',
          company: 'MinStock',
          location: 'Ciudad de Panamá, Panamá',
          period: 'Enero 2019 – Marzo 2020',
          points: [
            'Administré infraestructura IT: servidores Linux/Windows, redes y 50+ sistemas de usuario',
            'Desarrollé scripts de automatización para mantenimiento, monitoreo, análisis de logs y respuesta a incidentes',
            'Construí herramientas web internas (dashboards, sistema de tickets) y mantuve flujos de QA con pipelines CI/CD',
            'Implementé detección de intrusos, monitoreo de seguridad y procedimientos de recuperación ante desastres',
            'Reduje el tiempo de inactividad en 40% mediante monitoreo proactivo y alertas automatizadas',
          ],
        },
        {
          title: 'Técnico en Computación',
          company: 'Servicios Técnicos Carranza',
          location: 'Mexicali, México',
          period: 'Diciembre 2015 – Diciembre 2018',
          points: [
            'Diagnóstico de hardware, reparación, ensamblaje de PCs y mantenimiento preventivo para 100+ clientes',
            'Configuración de redes, despliegue de SO (Linux y Windows) y servicios de recuperación de datos',
            'Eliminación de malware, hardening de sistemas y auditorías de seguridad para pequeños negocios',
          ],
        },
      ],
    },
    skills: {
      title: 'Experiencia Técnica',
      categories: [
        {
          name: 'Desarrollo Full-Stack',
          icon: '💻',
          items: ['React', 'Next.js', 'TypeScript', 'Node.js', 'Python', 'FastAPI', 'Django', 'C#', '.NET', 'PHP', 'SQL', 'REST APIs', 'GraphQL', 'HTML/CSS', 'Tailwind CSS', 'Shell/Bash', 'Git'],
        },
        {
          name: 'Desarrollo de Videojuegos y Motores',
          icon: '🎮',
          items: ['Unity', 'Gamebryo / NetImmerse', 'Blender', 'Unreal Engine', 'Godot', 'Sistemas de Juego C++', 'Scripting Lua', 'Programación de Shaders', 'Pipelines de Assets 3D', 'Networking Multijugador', 'Arquitectura de Juegos'],
        },
        {
          name: 'Ciberseguridad y Seguridad Ofensiva',
          icon: '🔒',
          items: ['IDA Pro', 'Ghidra', 'Burp Suite', 'Metasploit', 'Nmap', 'Wireshark', 'x64dbg', 'Pruebas de Penetración', 'Análisis Binario', 'Ingeniería Inversa de Protocolos', 'Evaluación de Vulnerabilidades', 'Auditoría de Seguridad de Redes', 'OWASP Top 10', 'Hardening de Infraestructura', 'Respuesta a Incidentes', 'Competencias CTF'],
        },
        {
          name: 'Datos y Analítica',
          icon: '📊',
          items: ['MongoDB', 'PostgreSQL', 'SQL Server', 'Redis', 'Power BI', 'Analítica de Datos', 'Dashboards KPI', 'Pipelines ETL', 'Pandas', 'Visualización de Datos', 'Modelado Predictivo'],
        },
        {
          name: '3D y Visualización',
          icon: '🎨',
          items: ['Blender (Modelado, Rigging, Animación)', 'AutoCAD', 'Fotogrametría', 'Procesamiento de Nubes de Puntos', 'Desarrollo VR/AR', 'VRChat SDK', 'UdonSharp'],
        },
        {
          name: 'DevOps y Cloud',
          icon: '☁️',
          items: ['Administración Linux', 'Windows Server', 'Docker', 'Kubernetes', 'AWS', 'Azure', 'Nginx', 'CI/CD', 'Git/GitHub', 'Terraform', 'Ansible', 'Grafana', 'Prometheus'],
        },
      ],
    },
    projects: {
      title: 'Proyectos Destacados',
      items: [
        {
          name: 'Plataforma Empresarial Gruremex',
          description: 'Plataforma empresarial full-stack para operaciones de restaurantes con 15+ ubicaciones. Analítica de ventas y gastos, gestión de empleados con reconocimiento facial, control de inventario, costeo de recetas y dashboards multi-marca.',
          tech: ['FastAPI', 'MongoDB', 'Next.js 16', 'Python', 'TypeScript'],
          icon: '🏢',
        },
        {
          name: 'Capacita - Sistema de Gestión de Aprendizaje',
          description: 'LMS completo para el gobierno de Baja California. Gestión de cursos, 12 tipos de actividades interactivas, evaluaciones con TOTP 2FA, certificados digitales con firmas PGP, soporte offline PWA, i18n, notificaciones en tiempo real y accesibilidad WCAG 2.1 AA+.',
          tech: ['Next.js 16', 'MongoDB', 'TypeScript', 'React-PDF', 'Arquitectura DDD'],
          icon: '📚',
        },
        {
          name: 'Sistemas MMORPG y Arquitectura Live-Service',
          description: 'Estudio profundo de la arquitectura de servidores MMORPG y sistemas live-service: autenticación, flujo de personajes y zonas, combate en tiempo real, sistemas party/guild, comportamiento de NPC con pathfinding y patrones de BD escalables.',
          tech: ['C#', '.NET', 'Diseño de Sistemas de Juego', 'Programación de Redes', 'Arquitectura Live-Service'],
          icon: '⚔️',
        },
        {
          name: 'Ragnarok Online 2 — Herramientas para la Comunidad',
          description: 'Suite de herramientas para jugadores de MMORPG: calculadoras de probabilidad de mejora ("honing"), bases de datos y escáner de items, y un visor de chat en vivo. Construidas a partir de conocimiento profundo y de primera mano de la economía y progresión del juego, con backends de alto rendimiento y frontends web modernos.',
          tech: ['C++', 'Next.js', 'MongoDB', 'Redis', 'WebSocket'],
          icon: '🛠️',
        },
        {
          name: 'Contribuciones Open Source',
          description: 'Add-ons de Blender: Edge Slide Distance y TopoLens (overlay de topología en Edit Mode). Tooling open-source para desarrolladores publicado en GitHub.',
          tech: ['Python', 'C#', 'Blender API'],
          icon: '🌐',
        },
        {
          name: 'Investigación y Herramientas del Motor Gamebryo',
          description: 'Conocimiento profundo de la arquitectura del motor Gamebryo/NetImmerse y sus formatos de assets (NIF, KF, KFM): modelos 3D, animaciones esqueléticas y datos de escena. Desarrollo de herramientas de importación/exportación en Blender, utilidades de localización y modernización de proyectos legacy.',
          tech: ['Python', 'Blender', 'NIF/KF/KFM', 'Pipelines 3D', 'Localización'],
          icon: '🔬',
        },
      ],
    },
    education: {
      title: 'Educación',
      items: [
        {
          degree: 'Ingeniería en Sistemas Computacionales',
          school: 'Instituto Tecnológico de Mexicali',
          period: '2019 – 2026 · Egresado (título en trámite)',
          details: 'Enfoque: Arquitectura de Software, Sistemas de BD, Seguridad de Redes, Sistemas Distribuidos',
        },
        {
          degree: 'Preparatoria',
          school: 'Preparatoria y Universidad 16 de Septiembre',
          period: 'Graduado Julio 2019',
          details: '',
        },
      ],
      certifications: 'Aprendizaje continuo autodidacta en pruebas de penetración, ingeniería inversa, arquitectura cloud, internals de motores de juegos y desarrollo full-stack moderno.',
    },
    contact: {
      title: 'Trabajemos Juntos',
      subtitle: 'Trabajo remoto para clientes de EE.UU. e internacionales. Disponible para reuniones presenciales y consultorías en el Sur de California. Misma zona horaria que California (PST/PDT).',
      email: 'jordan@squema.group',
      phone: '+52 (686) 127-8767',
      discord: 'JordanRO2',
      location: 'Mexicali, BC, México',
      visa: 'Contratista remoto · Reuniones presenciales en el Sur de California',
      links: {
        linkedin: 'linkedin.com/in/jordanro2',
        github: 'github.com/JordanRO2',
        website: 'jordan.booth-stash.com',
        squema: 'squema.group',
      },
    },
  },
};
